import { useDeleteOfferProtocol } from '@services/offers/mutations/useDeleteOfferProtocol'
import { useOfferProtocolPreview } from '@services/offers/mutations/useOfferProtocolPreview'
import { useUploadOfferProtocol } from '@services/offers/mutations/useUploadOfferProtocol'
import { viewOfferProtocol } from '@services/offers/queries/useDownloadOfferProtocol'
import { useOfferProtocols } from '@services/offers/queries/useOfferProtocols'
import { Eye, FileCheck, FileText, Sparkles, Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Combobox } from '@/components/ui/combobox'
import { useConfirm } from '@/components/ui/confirm-dialog'

type ProtocolLanguage = 'sk' | 'en'

interface ProtocolRowProps {
  label: string
  protocol?: { id: string; name?: string }
  generating?: boolean
  uploading?: boolean
  disabled?: boolean
  onGenerate: () => void
  onUpload: (file: File) => void
  onView: (id: string) => void
  onDelete: (id: string) => void
}

function ProtocolRow({
  label,
  protocol,
  generating,
  uploading,
  disabled,
  onGenerate,
  onUpload,
  onView,
  onDelete
}: ProtocolRowProps) {
  const { t } = useTranslation('offers')
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-semibold text-ink">
          <FileCheck className="h-4 w-4 text-primary" />
          {label}
        </span>
        {!protocol && (
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={onGenerate}
              disabled={disabled || generating}
            >
              <Sparkles className="h-4 w-4" />
              {t('admin.protocols.generate')}
            </Button>
            <input
              type="file"
              accept="application/pdf"
              ref={inputRef}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) onUpload(file)
                e.target.value = ''
              }}
            />
            <Button
              size="sm"
              variant="ghostDark"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
            >
              <Upload className="h-4 w-4" />
              {t('admin.protocols.upload')}
            </Button>
          </div>
        )}
      </div>

      {protocol && (
        <Card variant="light" className="flex items-center justify-between gap-3 p-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-ink/[0.08] bg-white text-destructive">
              <FileText className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="truncate font-medium text-ink">
                {protocol.name}
              </div>
              <span className="mt-0.5 inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success">
                {t('admin.protocols.uploaded')}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            <Button
              variant="ghostDark"
              size="icon"
              aria-label={t('admin.protocols.view')}
              onClick={() => onView(protocol.id)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghostDark"
              size="icon"
              aria-label={t('admin.protocols.delete')}
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(protocol.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}

export function OfferProtocols({ offerId }: { offerId: string | undefined }) {
  const { t } = useTranslation('offers')
  const [language, setLanguage] = useState<ProtocolLanguage>('sk')
  const { confirm, dialog } = useConfirm()

  const { data: protocols = [] } = useOfferProtocols(offerId)
  const uploadProtocol = useUploadOfferProtocol()
  const deleteProtocol = useDeleteOfferProtocol()
  const previewProtocol = useOfferProtocolPreview()

  const handoverProtocol = protocols.find((p) => p.protocol_type === 'HANDOVER')
  const returnProtocol = protocols.find((p) => p.protocol_type === 'RETURN')

  const generate = (protocolType: 'handover' | 'return') => {
    if (!offerId) return
    previewProtocol.mutate({ offerId, protocolType, language })
  }
  const upload = (protocolType: 'HANDOVER' | 'RETURN') => (file: File) => {
    if (!offerId) return
    uploadProtocol.mutate({ offerId, file, protocolType })
  }
  const view = (protocolId: string) => {
    if (offerId) viewOfferProtocol({ offerId, protocolId })
  }
  const remove = async (protocolId: string) => {
    if (!offerId) return
    if (
      await confirm({
        title: t('admin.protocols.delete_confirm'),
        destructive: true,
        confirmText: t('admin.protocols.yes'),
        cancelText: t('admin.protocols.no')
      })
    ) {
      deleteProtocol.mutate({ offerId, protocolId })
    }
  }

  return (
    <Card className="space-y-5 p-5">
      {dialog}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="m-0 flex items-center gap-2.5 font-display text-base font-bold tracking-tight text-ink">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <FileCheck className="h-5 w-5" />
          </span>
          {t('admin.protocols.title')}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t('admin.protocols.language')}:
          </span>
          <Combobox
            value={language}
            onChange={(v) => setLanguage((v as ProtocolLanguage) ?? 'sk')}
            searchable={false}
            className="w-36"
            options={[
              { value: 'sk', label: 'Slovensky' },
              { value: 'en', label: 'English' }
            ]}
          />
        </div>
      </div>

      <div className="space-y-5">
        <ProtocolRow
          label={t('admin.protocols.return')}
          protocol={handoverProtocol}
          generating={previewProtocol.isPending}
          uploading={uploadProtocol.isPending}
          onGenerate={() => generate('handover')}
          onUpload={upload('HANDOVER')}
          onView={view}
          onDelete={remove}
        />
        <div className="h-px w-full bg-ink/[0.06]" />
        <ProtocolRow
          label={t('admin.protocols.handover')}
          protocol={returnProtocol}
          generating={previewProtocol.isPending}
          uploading={uploadProtocol.isPending}
          onGenerate={() => generate('return')}
          onUpload={upload('RETURN')}
          onView={view}
          onDelete={remove}
        />
      </div>
    </Card>
  )
}
